import { useSearchParams } from 'react-router-dom';
import { Tabs } from '../../design-system';
import OrgAdminLayout from '../components/OrgAdminLayout';
import TallyCompanies from '../components/tally/TallyCompanies';
import TallyBrowser from '../components/tally/TallyBrowser';
import TallyBooks from '../components/tally/TallyBooks';
import TallySyncOverview from '../components/tally/TallySyncOverview';
import '../styles/TallyPanel.css';

// The firm's whole Tally, at the firm's level — and it does TWO jobs.
//
//   Companies   which companies exist in the firm's Tally, and which of them
//               are clients here yet: scan a machine, create the missing
//               clients, bind a company to a client that already exists.
//   Books       the books of every company already bound — its ledgers, its
//               entries, the reports Tally computes for it, and the queue of
//               its parties that may be clients of the firm too.
//
// ⚠️ ONE PAGE WITH TWO TABS, NOT TWO PAGES. They are the same errand at two
// stages — get a company in, then read it — and a firm onboarding its Tally
// moves between them constantly. Two entries in the sidebar would also make the
// second one findable only by somebody who already knew it existed.
//
// ⚠️ WHY THE BOOKS ARE HERE AT ALL. Every view on the Books tab used to live
// inside a client record, one client at a time. All of them are things a
// practice does across its whole book: you browse ledgers hunting for
// something, you read entries reconciling a quarter, you clear a match queue in
// one sitting. Doing any of that meant opening a client, working, going back,
// opening the next client and re-choosing the same view. The company picker on
// that tab is that entire loop, deleted.
//
// ⚠️ WHAT STAYS ON THE CLIENT IS ONE REPORT, and only because it is genuinely a
// per-client question: what shape is this client in. See
// components/tally/TallyPosition.jsx, rendered by components/TallyPanel.jsx.
//
// The levels, stated once more because conflating them has cost real damage:
//
//   a Tally COMPANY  →  a client of the firm
//   a LEDGER inside  →  a customer of THAT client
//
// Confusing the two is what once turned a client's debtors into clients of the
// practice.

const TABS = [
  { key: 'companies', label: 'Companies' },
  // ⚠️ A SEPARATE TAB, NOT A MODE OF "Companies". That one scans a whole
  // disk and proposes; this one is a person walking their own folders and
  // deciding. Same data, opposite direction of trust — folding them together
  // would mean a screen that sometimes acts on its own and sometimes waits.
  { key: 'browse', label: 'Pick by folder' },
  { key: 'books', label: 'Books' },
];

function OrgAdminTally() {
  // ⚠️ THE TAB LIVES IN THE URL, not in state alone, so this page can be linked
  // INTO. A client's own Tally tab sends people straight to that company's
  // books — `?tab=books&company=<id>` — and without the tab in the query that
  // link could only ever land on the Companies list, leaving the reader to find
  // the right company again in a picker of two hundred.
  //
  // It also makes the browser's Back button behave: switching tab is a
  // navigation the reader can undo, which a useState would swallow.
  const [params, setParams] = useSearchParams();

  // ⚠️ OPENS ON COMPANIES, and the order of the tabs is the order of the work:
  // nothing can be read on the Books tab until a company has been bound on this
  // one, so a firm arriving for the first time lands on the only tab that can
  // do anything for them. An unrecognised ?tab= falls back here rather than
  // rendering nothing.
  const raw = params.get('tab');
  const tab = TABS.some((t) => t.key === raw) ? raw : 'companies';

  const setTab = (next) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set('tab', next);
    // ⚠️ THE COMPANY GOES WITH THE TAB IT BELONGS TO. Leaving `company` behind
    // when moving to Companies would put a stale id back in play the moment
    // somebody returned to Books by hand.
    if (next !== 'books') { nextParams.delete('company'); nextParams.delete('view'); }
    // `replace`: flipping a tab is not a page worth pressing Back through
    // twice, and the deep link that brought the reader here should stay the
    // entry they return to.
    setParams(nextParams, { replace: true });
  };

  return (
    <OrgAdminLayout
      title="Tally"
      subtitle="The companies in your Tally, and the books of the ones you have connected"
    >
      {/* ⚠️ ABOVE THE TABS, so it is on the screen the section OPENS on.
          Syncing is manual now, and the per-company Sync view lives three
          clicks in — behind the Books tab and a company picker. A firm with
          forty connections had forty places to look to find the one that is
          stuck, which is the same as having none. It renders nothing at all
          until a company is connected, so the empty state below is untouched. */}
      <TallySyncOverview />

      <Tabs active={tab} onChange={setTab} layoutId="tally-firm-tabs" tabs={TABS} />

      {/* Rendered conditionally rather than hidden, so switching tabs REMOUNTS.
          Both halves load on mount, and the Books tab has to pick up a company
          bound seconds earlier on the Companies tab — keeping it mounted would
          leave it listing the connections as they stood when the page opened. */}
      {tab === 'companies' && <TallyCompanies />}
      {tab === 'browse' && <TallyBrowser />}
      {tab === 'books' && <TallyBooks />}
    </OrgAdminLayout>
  );
}

export default OrgAdminTally;
